import { Client } from "@/Common/Api/Client";
import { FavoriteControl } from "@/Common/Components/Favorites/FavoriteControl";
import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { PinItemControl } from "@/Common/Components/IconControls/PinItemControl";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { SingleSelectTextBoxControl } from "@/Common/Components/Inputs/SingleSelectTextBoxControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RvqPreferenceType } from "../Constants/RvqPreferenceType";
import { ValueSetType } from "../Constants/ValueSetType";
import { BaseRvqPreference } from "../Models/BaseRvqPreference";
import { LocationModel } from "../Models/LocationModel";
import { MedicationBatchModel } from "../Models/MedicalBatchModel";
import { PractitionerModel } from "../Models/PractitionerModel";
import { RvqDomainValue } from "../Models/RvqDomainValue";
import { RvqLocationPreference } from "../Models/RvqLocationPreference";
import { RvqMedicationPreference } from "../Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "../Models/RvqPractitionerPreference";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { GetRvqMedicationByTradeNameResponse } from "../ResponseModels/GetRvqMedicationByTradeNameRequest";
import { GetRvqPractitionersBySearchResponse } from "../ResponseModels/GetRvqPractitionersBySearchResponse";
import { GetRvqPreferencesResponse } from "../ResponseModels/GetRvqPreferencesResponse";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";
import { RvqPractitionerSearch } from "./RvqPractitionerSearch";

interface ImmunizationPreferencesProps extends IBaseImmunizationProfileSectionProps {
    currentMedicationPreferences: RvqMedicationPreference[];
    currentPractitionerPreferences: RvqPractitionerPreference[];
    currentLocationPreferences: RvqLocationPreference[];
    onClose: () => void;
    updatePreferences: (preferences: RvqMedicationPreference[], practitionerPreferences: RvqPractitionerPreference[], locationPreferences: RvqLocationPreference[]) => void;
}

class ImmunizationPreferencesState extends BaseImmunizationProfileSectionState {
    public currentSelectedCategory: RvqPreferenceType;

    public selected: {
        [RvqPreferenceType.Location]: RvqLocationPreference[],
        [RvqPreferenceType.Medication]: RvqMedicationPreference[],
        [RvqPreferenceType.Practitioner]: RvqPractitionerPreference[]
    };

    public options: {
        practitioners: RvqPractitionerPreference[],
        locations: RvqLocationPreference[],
        immunizingProducts: RvqMedicationPreference[]
    };

    public practitioner: {
        givenName: string,
        familyName: string,
        role: string,
        permitNumber: string,
        searchedPractitionerList: PractitionerModel[],
    };

    public medication: {
        currentBatchNumberSelected: string,
        selectedTradeName: string;
        searchField: string;
    };

    public location: {
        name: string;
        currentLocations: LocationModel[];
        showLocationOptions: boolean;
    };

    constructor(init?: Partial<ImmunizationPreferencesState>) {
        super(init);
    }

    public static default(): ImmunizationPreferencesState {
        return new ImmunizationPreferencesState({
            ...BaseImmunizationProfileSectionState.default(),
            currentSelectedCategory: RvqPreferenceType.Practitioner,
            selected: {
                [RvqPreferenceType.Location]: [],
                [RvqPreferenceType.Medication]: [],
                [RvqPreferenceType.Practitioner]: []
            },

            options: {
                practitioners: [],
                locations: [],
                immunizingProducts: []
            },

            practitioner: {
                // Search parameters
                givenName: "",
                familyName: "",
                role: null,
                permitNumber: "",
                searchedPractitionerList: []
            },
            medication: {

                // Search parameters
                currentBatchNumberSelected: "",
                selectedTradeName: null,
                searchField: ""
            },

            location: {
                name: "",
                currentLocations: [],
                showLocationOptions: false
            },

            isLoading: false
        });
    }

    public static eraseSearchFields(state: ImmunizationPreferencesState): void {
        state.location = {
            name: "",
            showLocationOptions: false,
            currentLocations: []
        };
        state.practitioner = {
            givenName: "",
            familyName: "",
            role: null,
            permitNumber: "",
            searchedPractitionerList: []
        };
        state.medication = {
            searchField: "",
            selectedTradeName: "",
            currentBatchNumberSelected: ""
        };
    }
}

class ImmunizationPreferences extends BaseImmunizationProfileSection<ImmunizationPreferencesState, ImmunizationPreferencesProps> {
    private locationOptionsDropDown: HTMLDivElement;
    private practitionerOptionsDropDown: HTMLDivElement;

    protected get requestValues(): GetValuesRequest {
        return new GetValuesRequest({
            Type: [
                ValueSetType.TradeName,
                ValueSetType.PractitionnerRole
            ]
        });
    }

    private get options(): { category: RvqPreferenceType, labelKey: string }[] {
        return [
            { category: RvqPreferenceType.Practitioner, labelKey: "QuebecVaccinationRegistry_VaccinatorsLabel" },
            { category: RvqPreferenceType.Location, labelKey: "QuebecVaccinationRegistry_ImmunizationLocationsLabel" },
            { category: RvqPreferenceType.Medication, labelKey: "QuebecVaccinationRegistry_ImmunizingProductsLabel" }
        ];
    }

    public constructor(props: ImmunizationPreferencesProps) {
        super(props);
        this.handleClickOutSide = this.handleClickOutSide.bind(this);

        const initialState = ImmunizationPreferencesState.default();
        initialState.selected = {
            [RvqPreferenceType.Medication]: props.currentMedicationPreferences.map(p => new RvqMedicationPreference(p)),
            [RvqPreferenceType.Practitioner]: props.currentPractitionerPreferences.map(p => new RvqPractitionerPreference(p)),
            [RvqPreferenceType.Location]: props.currentLocationPreferences.map(p => new RvqLocationPreference(p))
        };

        this.state = initialState;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        document.body.addEventListener("click", this.handleClickOutSide);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        document.body.removeEventListener("click", this.handleClickOutSide);
    }

    public getBody(): JSX.Element {
        let listMaxHeight = "160px";
        if (this.state.currentSelectedCategory === RvqPreferenceType.Medication)
            listMaxHeight = "340px";
        else if (this.state.currentSelectedCategory === RvqPreferenceType.Location)
            listMaxHeight = "470px";

        return (
            <>
                <div className="row">
                    <div className="col-md-12 side-by-side-tabs">
                        {this.options.map(
                            x => {
                                const titleLabel = cacheStore.GetString(x.labelKey).toUpperCase();
                                return (
                                    <span key={x.category}>
                                        {this.state.currentSelectedCategory === x.category
                                            ? titleLabel
                                            : (
                                                <a
                                                    onClick={() => this.mutateState(s => {
                                                        s.currentSelectedCategory = x.category;
                                                        s.options = {
                                                            practitioners: [],
                                                            locations: [],
                                                            immunizingProducts: []
                                                        };
                                                        s.medication = {
                                                            selectedTradeName: "",
                                                            currentBatchNumberSelected: "",
                                                            searchField: ""
                                                        };
                                                    })}
                                                >
                                                    {titleLabel}
                                                </a>
                                            )
                                        }
                                    </span>
                                );
                            }
                        )}
                    </div>
                </div>
                <div className="row">
                    {this.renderFormFields()}
                </div>
                <div className="row">
                    <div
                        className="col-md-12"
                        style={{ marginTop: "10px" }}
                    >
                        <div style={{ maxHeight: listMaxHeight, overflowY: "auto" }}>
                            <div style={{ borderTop: "solid 1px #eee", width: "100%", marginBottom: "10px" }} />
                            {this.renderExistingList()}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    private renderItem<TPreference extends BaseRvqPreference>(preference: TPreference, closeDropDownOnAction: boolean = true, renderRemoveButton: boolean = false): JSX.Element {
        return (
            <div className="pointer immunization-dropdown-item" style={{ marginBottom: "5px", justifyContent: "flex-start" }} key={preference.ItemId}>
                <span>
                    <PinItemControl
                        onPinItemClick={isPinned => this.setPinnedItem(preference, isPinned, closeDropDownOnAction)}
                        isPinned={preference.IsPinned}
                    />
                </span>
                <span>
                    <FavoriteControl
                        isFavoriteSet={preference.IsFavorite}
                        onChange={isFavorite => this.setFavoriteItem(preference, isFavorite, closeDropDownOnAction)}
                        imageStyles={{ height: "20px" }}
                    />
                </span>
                <span
                    className="ellipsis-auto"
                    style={{ color: "#0070d2" }}
                    onClick={() => closeDropDownOnAction && this.onAddPreference(preference)}
                >
                    {preference.DisplayName}
                </span>
                {renderRemoveButton && (
                    <span
                        id={"removeItem" + sessionStore.newId()}
                        className="pointer"
                        style={{ color: "red", fontWeight: "bold" }}
                        onClick={e => {
                            e.stopPropagation();
                            this.onRemovePreference(preference);
                        }}
                    >
                        X
                    </span>
                )}
            </div>
        );
    }

    private renderExistingList(): JSX.Element[] {
        const type: RvqPreferenceType = this.state.currentSelectedCategory;

        if (type === RvqPreferenceType.Practitioner)
            return this.renderPractitionerList();

        return QuebecVaccinationRegistryHelper
            .sortBasePreferenceArray<BaseRvqPreference>(this.state.selected[type])
            .map(p => this.renderItem(p, true, type === RvqPreferenceType.Medication));
    }

    private renderPractitionerList(): JSX.Element[] {
        const currentPractitionerPreferences = this.getSortedPreferencesForDropDown<RvqPractitionerPreference>(RvqPreferenceType.Practitioner, true);
        const params = [
            this.state.practitioner.givenName.trim(),
            this.state.practitioner.familyName.trim(),
            this.state.practitioner.permitNumber.trim()
        ];

        const filteredPreferences = currentPractitionerPreferences
            .filter(x => !this.state.practitioner.searchedPractitionerList?.any() || params.any(param => param && x.Value.contains(param, { ignoreCase: true, ignoreDiacritics: true })));

        const filteredPractitioners = this.state.practitioner.searchedPractitionerList
            .map(loc => PractitionerModel.toPreference(loc))
            .filter(searchedPref => !filteredPreferences.any(
                filteredPref => filteredPref.Model.QuebecVaccinationRegistryId === searchedPref.QuebecVaccinationRegistryId
            ));

        const resultRvqPreference = QuebecVaccinationRegistryHelper
            .sortBasePreferenceArray<RvqPractitionerPreference>(filteredPractitioners)
            .map(x => new SearchableItemModelT<RvqPractitionerPreference>(x.DisplayName, x.ItemId.toString(), x, ""));

        return [
            ...filteredPreferences.map(item => (
                <li key={item.Model.ItemId}>
                    {this.renderItem(item.Model, false)}
                </li>
            )),
            ...resultRvqPreference.map(item => (
                <li key={item.Model.ItemId}>
                    {this.renderItem(item.Model, true)}
                </li>
            ))
        ];
    }

    private setFavoriteItem<TPreference extends BaseRvqPreference>(preference: TPreference, isFavorite: boolean, closeDropDown: boolean = true): void {
        const type = preference.Type;

        this.mutateState(s => {
            const match = s.selected[type].firstOrDefault(p => p.comparableRvqId === preference.comparableRvqId);

            if (match) {
                match.IsFavorite = isFavorite;
                if (!isFavorite)
                    match.IsPinned = false;
            }
            else {
                preference.IsFavorite = isFavorite;
                s.selected[type] = [...s.selected[type], preference as any];
                ImmunizationPreferencesState.eraseSearchFields(s);
            }

            s.location.showLocationOptions = !closeDropDown;
        });
    }

    private setPinnedItem<TPreference extends BaseRvqPreference>(preference: TPreference, isPinned: boolean, closeDropDown: boolean = false): void {
        const type = preference.Type;

        this.mutateState(s => {
            for (let pref of s.selected[type])
                pref.IsPinned = false;

            const match = s.selected[type].firstOrDefault(x => x.comparableRvqId === preference.comparableRvqId);

            if (match) {
                match.IsPinned = isPinned;
                match.IsFavorite = isPinned || match.IsFavorite;
            }
            else {
                preference.IsPinned = isPinned;
                preference.IsFavorite = isPinned;
                s.selected[type] = [...s.selected[type], preference as any];
                ImmunizationPreferencesState.eraseSearchFields(s);
            }

            s.location.showLocationOptions = !closeDropDown;
        });
    }

    private renderFormFields(): JSX.Element {
        switch (this.state.currentSelectedCategory) {
            case RvqPreferenceType.Practitioner:
                return this.renderPractitionerPreferenceSection();
            case RvqPreferenceType.Location:
                return this.renderLocationPreferenceSection();
            case RvqPreferenceType.Medication:
                return this.renderMedicationPreferenceSection();
        }
    }

    private renderMedicationPreferenceSection(): JSX.Element {
        const selectedTradeName = this.state.medication.selectedTradeName;
        const currentBatchNumberSelected = this.state.medication.currentBatchNumberSelected;
        const tradeNames = this.state.values?.Types?.[ValueSetType.TradeName] ?? {};

        return (
            <div className="col-md-12" style={{ marginTop: "20px" }}>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_CommercialNameLabel"
                    isEditMode
                >
                    <SingleSelectTextBoxControl
                        onRemove={() => this.mutateState(s => {
                            s.medication.selectedTradeName = null;
                            s.medication.currentBatchNumberSelected = null;
                            s.options.immunizingProducts = [];
                        })}
                        limit={null}
                        displayText={selectedTradeName}
                        isSelected={!!selectedTradeName}
                        onChange={val => this.mutateState(s => s.medication.searchField = val)}
                        autoCompleteItems={Immutable.List(Object.keys(tradeNames).map(key => new SearchableItemModelT<RvqDomainValue>(tradeNames[key]?.Label, key)))}
                        onAutoCompleteSelectCallBack={code => {
                            this.mutateState(
                                s => {
                                    s.medication.selectedTradeName = code;
                                    s.medication.searchField = "";
                                },
                                () => this.getMedicationByTradeName(code)
                            );
                        }}
                        value={this.state.medication.searchField || ""}
                    />
                </FormFieldRow>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_LotNumberLabel"
                    isEditMode
                >
                    <SelectKVPControl
                        Callback={val => this.mutateState(s => s.medication.currentBatchNumberSelected = val)}
                        AvailableValues={this.state.options.immunizingProducts.map(x => new KeyValuePair(x.ItemId.toString(), x.DisplayName)) ?? []}
                        Value={currentBatchNumberSelected}
                        IncludeEmptyChoice
                    />
                </FormFieldRow>
                <div style={{ marginTop: "20px", width: "100%" }}>
                    <button
                        disabled={!(selectedTradeName && currentBatchNumberSelected) || this.state.isLoading}
                        className="btn btn-primary pull-right"
                        onClick={() => {
                            const pref = this.state.options.immunizingProducts.firstOrDefault(x => x.ItemId.toString() === this.state.medication.currentBatchNumberSelected);
                            this.onAddMedicationPreference(
                                pref,
                                () => this.mutateState(s => {
                                    s.medication = {
                                        ...s.medication,
                                        currentBatchNumberSelected: "",
                                        selectedTradeName: ""
                                    };
                                    s.options.immunizingProducts = [];
                                })
                            );
                        }}
                    >
                        {cacheStore.GetString("AddLabel")}
                    </button>
                </div>
            </div>
        );
    }

    private getSortedPreferencesForDropDown<TPreference extends BaseRvqPreference>(type: RvqPreferenceType, getAll: boolean = false): SearchableItemModelT<TPreference>[] {
        return QuebecVaccinationRegistryHelper
            .sortBasePreferenceArray<BaseRvqPreference>(this.state.selected[type])
            ?.filter(p => getAll || p.IsFavorite || p.IsPinned)
            .map(p => new SearchableItemModelT<TPreference>(p.DisplayName, p.ItemId.toString(), p as TPreference, ""));
    }

    private renderLocationPreferenceSection(): JSX.Element {
        const filteredPreferences = this
            .getSortedPreferencesForDropDown<RvqLocationPreference>(RvqPreferenceType.Location, true)
            .filter(x => x.Value.contains(this.state.location.name, { ignoreCase: true, ignoreDiacritics: true }));

        const filteredLocations = this.state.location.currentLocations
            .map(loc => LocationModel.toPreference(loc))
            .filter(x => !filteredPreferences.any(f => f.Model.QuebecVaccinationRegistryId === x.QuebecVaccinationRegistryId));

        const resultRvqPreference = QuebecVaccinationRegistryHelper
            .sortBasePreferenceArray<RvqLocationPreference>(filteredLocations)
            .map(x => new SearchableItemModelT<RvqLocationPreference>(x.DisplayName, x.ItemId.toString(), x, ""));

        return (
            <div className="col-md-12" style={{ marginTop: "20px" }}>
                <div className="row">
                    <div className="col-md-12">
                        <label className="font-weight-bold">{cacheStore.GetString("QuebecVaccinationRegistry_ImmunizationLocationsLabel")}</label>
                        <TextboxControl
                            id="locationName"
                            listMaxHeight="250px"
                            onAutoCompleteSelectClearTextBox
                            onChange={async text => {
                                this.mutateState(
                                    state => {
                                        state.location.name = text;
                                        state.location.showLocationOptions = !!text;
                                        state.location.currentLocations = [];
                                    },
                                    () => window.setTimeout(
                                        async () => {
                                            if (IsNullOrWhiteSpace(text) || text != this.state.location.name)
                                                return; /* Wait for next debounce */

                                            let result = await this.handleLocationSearch(text);
                                            this.mutateState(s => s.location.currentLocations = result);
                                        },
                                        400
                                    )
                                );
                            }}
                            onFocus={() => this.mutateState(s => s.location.showLocationOptions = !IsNullOrWhiteSpace(s.location.name))}
                            value={this.state.location.name}
                        />
                        <div
                            ref={ref => this.locationOptionsDropDown = ref}
                            style={{ width: "100%", position: "relative" }}
                            onClick={e => e.stopPropagation()}
                        >
                            {resultRvqPreference?.any() && this.state.location.showLocationOptions && (
                                <div
                                    className="list-container-txtbox"
                                    id="location-list-container"
                                    style={{ zIndex: 2000, position: "absolute", top: 0, left: 0, maxHeight: "200px", width: "100%" }}
                                >
                                    <div className="results-container">
                                        <ul className="results-list">
                                            {filteredPreferences.map(item => (
                                                <li key={item.Model.ItemId}>
                                                    {this.renderItem(item.Model, false)}
                                                </li>
                                            ))}
                                            {resultRvqPreference.map(item => (
                                                <li key={item.Model.ItemId}>
                                                    {this.renderItem(item.Model, true)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    private handleClickOutSide(event: MouseEvent): void {
        if (!(event.target instanceof Element))
            return;

        if (
            (this.locationOptionsDropDown?.contains?.(event.target) === false && event.target.id !== "locationName")
            || this.practitionerOptionsDropDown?.contains?.(event.target) === false
        ) {
            this.mutateState(s => s.location.showLocationOptions = false);
        }
    }

    private renderPractitionerPreferenceSection(): JSX.Element {
        return (
            <div className="col-md-12" style={{ marginTop: "20px" }}>
                <div className="row">
                    <div className="col-md-12">
                        <RvqPractitionerSearch
                            searchFields={this.state.practitioner}
                            onChange={(value, key) => this.mutateState(s => {
                                s.practitioner[key] = value;
                                s.practitioner.searchedPractitionerList = [];
                            })}
                            practitioners={this.state.practitioner.searchedPractitionerList}
                            roles={this.state.values?.Types?.[ValueSetType.PractitionnerRole] ?? {}}
                            onPractitionerSelect={() => { }}
                            showList={false}
                            onEnterKeyDown={() => this.getPractitionersBySearch()}
                            onSearchClick={() => this.getPractitionersBySearch()}
                            isLoading={this.state.isLoading}
                            disableSearchButton={!this.canSearchPractitioner()}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private canSearchPractitioner(): boolean {
        const {
            givenName,
            familyName,
            permitNumber
        } = this.state.practitioner;

        return (
            !IsNullOrWhiteSpace(givenName)
            || !IsNullOrWhiteSpace(familyName)
            || !IsNullOrWhiteSpace(permitNumber)
        ) && !this.state.isLoading;
    }

    private async getPractitionersBySearch(): Promise<void> {
        const {
            givenName,
            familyName,
            permitNumber,
            role
        } = this.state.practitioner;

        if (!this.canSearchPractitioner())
            return;

        this.mutateState(
            s => s.isLoading = true,
            async () => {
                const response = await Client.postAsync<GetRvqPractitionersBySearchResponse>(
                    ServerOperations.Server.QuebecVaccinationRegistry_GetPractitionersBySearch,
                    {
                        GivenName: givenName,
                        FamilyName: familyName,
                        PermitNumber: permitNumber,
                        RoleCode: role
                    }
                );
                const practitioners = response?.data?.Practitioners ?? [];
                this.mutateState(s => {
                    s.practitioner.searchedPractitionerList = practitioners;
                    s.isLoading = false;
                });
            }
        );
    }

    protected async onSave(): Promise<void> {
        this.mutateState(
            s => s.isLoading = true,
            async () => {
                const response = await Client.postAsync<GetRvqPreferencesResponse>(
                    ServerOperations.Server.QuebecVaccinationRegistry_SavePreferences,
                    {
                        MedicationPreferences: this.state.selected[RvqPreferenceType.Medication],
                        PractitionerPreferences: this.state.selected[RvqPreferenceType.Practitioner],
                        LocationPreferences: this.state.selected[RvqPreferenceType.Location]
                    }
                );
                this.mutateState(
                    s => s.isLoading = false,
                    () => {
                        if (response.isSuccess) {
                            this.props.updatePreferences(
                                response.data.MedicationPreferences.map(p => new RvqMedicationPreference(p)),
                                response.data.PractitionerPreferences.map(p => new RvqPractitionerPreference(p)),
                                response.data.LocationPreferences.map(p => new RvqLocationPreference(p))
                            );
                        }
                    }
                );
            }
        );
    }

    private onAddPreference<TPreference extends BaseRvqPreference>(preference: TPreference, then?: () => void): void {
        const type = preference.Type;

        this.mutateState(
            s => {
                const match = s.selected[type].firstOrDefault(p => p.comparableRvqId == preference.comparableRvqId);

                if (match)
                    match.DisplayName = preference.DisplayName;
                else if (!s.selected[type].any(x => x.ItemId === preference.ItemId))
                    s.selected[type].push(preference as any);

                ImmunizationPreferencesState.eraseSearchFields(s);
            },
            () => then?.()
        );
    }

    private onAddMedicationPreference(preference: RvqMedicationPreference, then?: () => void): void {
        this.mutateState(
            s => {
                const previousPreference = s.selected[RvqPreferenceType.Medication].firstOrDefault(x => x.QuebecVaccinationRegistryId === preference.QuebecVaccinationRegistryId);
                if (previousPreference) {
                    preference.IsPinned = previousPreference.IsPinned;
                    preference.IsFavorite = previousPreference.IsFavorite;
                }
                s.selected[RvqPreferenceType.Medication] = s.selected[RvqPreferenceType.Medication].filter(x => x.QuebecVaccinationRegistryId !== preference.QuebecVaccinationRegistryId);
                s.selected[RvqPreferenceType.Medication].push(preference);

                ImmunizationPreferencesState.eraseSearchFields(s);
            },
            () => then?.()
        );
    }

    private onRemovePreference<TPreference extends BaseRvqPreference>(preference: TPreference): void {
        const type = preference.Type;

        this.mutateState(s => {
            // note: we can't use .filter() due to a TypeScript build error (it can't match the generic type to the method signature)
            const list = s.selected[type];
            for (let i = list.length - 1; i >= 0; i--) {
                if (list[i].ItemId === preference.ItemId)
                    list.splice(i, 1);
            }
        });
    }

    private async getMedicationByTradeName(tradeName: string): Promise<void> {
        this.mutateState(
            s => s.isLoading = true,
            async () => {
                const response = await Client.postAsync<GetRvqMedicationByTradeNameResponse>(
                    ServerOperations.Server.QuebecVaccinationRegistry_GetMedicationByTradeName,
                    {
                        TradeName: tradeName
                    }
                );
                const medication = response?.data?.Medication;
                const batches = (medication?.Batches ?? []).map(x => MedicationBatchModel.toRvqPreference(medication, x));
                this.mutateState(s => {
                    s.options.immunizingProducts = batches;
                    if (batches.length === 1)
                        s.medication.currentBatchNumberSelected = batches[0].ItemId.toString();
                    s.isLoading = false;
                });
            }
        );
    }
}

export { ImmunizationPreferences };
